import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const LEVEL_COLOR = {
  PEMULA:   { bg: "#00E67622", border: "#00E676", text: "#00E676" },
  DASAR:    { bg: "#00A8CC22", border: "#00A8CC", text: "#00A8CC" },
  MENENGAH: { bg: "#A855F722", border: "#A855F7", text: "#A855F7" },
  LANJUTAN: { bg: "#FFD70022", border: "#FFD700", text: "#FFD700" },
};

function XPBar({ current, max, color = "var(--accent)" }) {
  const pct = Math.min((current / max) * 100, 100);
  return (
    <div style={{ background: "var(--border)", borderRadius: 99, height: 6, overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 99, transition: "width 0.8s ease" }} />
    </div>
  );
}

export default function AcademyHome() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch("/api/academy/courses", { headers }).then(r => r.json()),
      fetch("/api/academy/stats", { headers }).then(r => r.json()),
    ]).then(([coursesRes, statsRes]) => {
      if (coursesRes.ok) setCourses(coursesRes.data || []);
      if (statsRes.ok) setStats(statsRes.stats);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
      <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Loading Academy...</div>
    </div>
  );

  const totalLessons = courses.reduce((a, c) => a + c.lesson_count, 0);
  const completedLessons = courses.reduce((a, c) => a + c.completed_lessons, 0);
  const overallProgress = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  const xpToNextLevel = ((stats?.level || 1)) * 500;

  return (
    <div className="page">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 28 }}></span>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "var(--text-main)" }}>
              Alpha Price Action Academy
            </h1>
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
              Learn to trade like institutions — zero indicators, pure structure
            </p>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div style={{
          background: "var(--bg-surface)", border: "1px solid var(--border)",
          borderRadius: "var(--radius-xl)", padding: "20px 24px", marginBottom: 24,
          display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr", gap: 20,
          alignItems: "center"
        }} className="academy-stats-grid">
          <div>
            <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>Level</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: "var(--accent)", lineHeight: 1 }}>{stats.level}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>XP Total</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "#FFD700", lineHeight: 1 }}>  {stats.xp_total.toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>Streak</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "#FF6B35", lineHeight: 1 }}>  {stats.streak_days}d</div>
          </div>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Level {stats.level} → {stats.level + 1}</span>
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{stats.xp_total} / {xpToNextLevel} XP</span>
            </div>
            <XPBar current={stats.xp_total % 500} max={500} color="#FFD700" />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Overall Progress</span>
              <span style={{ fontSize: 11, color: "var(--accent)", fontWeight: 700 }}>{overallProgress}% ({completedLessons}/{totalLessons} lessons)</span>
            </div>
            <XPBar current={completedLessons} max={totalLessons || 1} color="var(--accent)" />
          </div>
        </div>
      )}

      {/* Course Grid */}
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: "var(--text-main)" }}>
          Learning Path
        </h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {courses.map((course, idx) => {
            const lc = LEVEL_COLOR[course.level] || LEVEL_COLOR.PEMULA;
            const isLocked = course.is_premium && (stats?.level || 1) < 3;
            return (
              <div key={course.id}
                onClick={() => !isLocked && navigate(`/academy/courses/${course.slug}`)}
                style={{
                  background: "var(--bg-surface)", border: `1px solid var(--border)`,
                  borderLeft: `4px solid ${course.color_hex}`,
                  borderRadius: "var(--radius-xl)", padding: "20px 24px",
                  cursor: isLocked ? "not-allowed" : "pointer",
                  opacity: isLocked ? 0.6 : 1,
                  transition: "all 0.15s",
                  display: "flex", gap: 16, alignItems: "center",
                }}>
                <div style={{
                  width: 52, height: 52, borderRadius: "var(--radius-lg)",
                  background: `${course.color_hex}22`, border: `1px solid ${course.color_hex}44`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 26, flexShrink: 0
                }}>
                  {course.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: "var(--text-main)" }}>{course.title}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99,
                      background: lc.bg, border: `1px solid ${lc.border}`, color: lc.text
                    }}>{course.level}</span>
                    {course.is_premium && <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: "#FFD70022", border: "1px solid #FFD700", color: "#FFD700" }}>   PREMIUM</span>}
                    {isLocked && <span style={{ fontSize: 10, color: "var(--text-muted)" }}> Unlock at Level 3</span>}
                  </div>
                  <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>{course.description}</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}> {course.lesson_count} lessons</span>
                    <span style={{ fontSize: 11, color: "#FFD700" }}>   {course.xp_reward} XP</span>
                    {course.progress > 0 && (
                      <span style={{ fontSize: 11, color: course.color_hex, fontWeight: 700 }}>
                        {Math.round(course.progress)}% complete
                      </span>
                    )}
                  </div>
                  {course.lesson_count > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <XPBar current={course.completed_lessons} max={course.lesson_count} color={course.color_hex} />
                    </div>
                  )}
                </div>
                <div style={{ color: "var(--text-muted)", fontSize: 20, flexShrink: 0 }}>›</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Daily tip */}
      <div style={{
        marginTop: 24, background: "var(--bg-surface)",
        border: "1px solid var(--accent-border)", borderLeft: "4px solid var(--accent)",
        borderRadius: "var(--radius-xl)", padding: "16px 20px"
      }}>
        <div style={{ fontSize: 11, color: "var(--accent)", fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>
             APA Principle
        </div>
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.7 }}>
          "The market does not move randomly. Every significant move leaves structural footprints.
          Your job is not to predict — it's to read."
        </p>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .academy-stats-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
}
